import React, { useState, type JSX } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

interface DialogData {
  title: string
  body: string | JSX.Element
  confirm: { text: string; callback: () => Promise<void> }
  cancel?: { text: string; callback?: () => Promise<void> }
  /** A checkbox the user must tick before Confirm is enabled, for a step that can't be undone (T4.20). */
  acknowledge?: string
}
export function useConfirmDialog() {
  const [dialogData, setDialogData] = useState<DialogData | undefined>(undefined)
  const [confirming, setConfirming] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const { t } = useTranslation()

  const onCancel = () => {
    if (dialogData?.cancel?.callback) {
      dialogData?.cancel?.callback()
    }
    setDialogData(undefined)
  }

  const onConfirm = async () => {
    if (dialogData?.confirm?.callback) {
      setConfirming(true)
      try {
        await dialogData.confirm.callback()
      } catch {
        return
      } finally {
        setConfirming(false)
      }
    }
    setDialogData(undefined)
  }
  const isOpen = typeof dialogData !== 'undefined'
  const showDialog = (dialogData: DialogData) => {
    const data = dialogData
    if (typeof data.cancel === 'undefined') {
      data.cancel = { text: t('general.cancel') }
    }
    setAcknowledged(false)
    setDialogData(dialogData)
  }
  const dialogComponent = (
    <Dialog
      open={isOpen}
      onClose={confirming ? undefined : () => setDialogData(undefined)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle>{dialogData?.title ?? ''}</DialogTitle>
      <DialogContent>
        {typeof dialogData?.body === 'string' ? (
          <DialogContentText style={{ whiteSpace: 'pre-line' }}>
            {dialogData?.body ?? ''}
          </DialogContentText>
        ) : (
          dialogData?.body
        )}
        {dialogData?.acknowledge ? (
          <FormControlLabel
            className="mt-3"
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={confirming}
              />
            }
            label={dialogData.acknowledge}
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="secondary" disabled={confirming}>
          {dialogData?.cancel?.text ?? ''}
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          autoFocus
          disabled={confirming || (Boolean(dialogData?.acknowledge) && !acknowledged)}
        >
          {confirming ? <CircularProgress size={20} /> : (dialogData?.confirm?.text ?? '')}
        </Button>
      </DialogActions>
    </Dialog>
  )
  return { showDialog, dialogComponent }
}
